import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useMutation } from "@apollo/client";
import {
  AUTHENTICATE_MUTATION,
  CREATE_USER_MUTATION,
  REFRESH_TOKEN_MUTATION,
} from "../api/graphql/mutations";
import {
  setAnalyticsUserId,
  clearAnalyticsUserId,
} from "../../../services/analytics";
import { ClientTrackingInput } from "../../../services/metaPixel";
import { clearSharedCache } from "../../../services/sharedStore";
import graphQLClient from "../../../services/gqlClients";
import tokenService, {
  StorageType,
} from "../../../services/tokenService";
import { useUserCache } from "../../../context/UserCacheContext";

interface AuthContextType {
  isAuthenticated: boolean;
  identityId: string | null;
  login: (
    username: string,
    password: string,
    rememberMe: boolean
  ) => Promise<void>;
  registerUser: (
    name: string,
    lastname: string,
    username: string,
    password: string,
    invitationCode: string,
    birthDate: Date,
    phoneNumber: string,
    gender: string,
    acceptTerms: boolean,
    acceptPrivacy: boolean,
    acquisitionSource?: any,
    promoCode?: string,
    marketingOptIn?: string,
    whatsappOptIn?: boolean,
    clientTracking?: ClientTrackingInput
  ) => Promise<any>;
  logout: () => void;
  accessToken: string | null;
  loading: boolean;
  hasRole: (role: string) => boolean;
  authInitialized: boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authenticate] = useMutation(AUTHENTICATE_MUTATION);
  const [createUser] = useMutation(CREATE_USER_MUTATION);
  const [refreshTokenMutation] = useMutation(REFRESH_TOKEN_MUTATION);
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);
  const [identityId, setIdentityId] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isAuthenticated, setAuthenticated] = useState<boolean>(false);
  const [authInitialized, setAuthInitialized] = useState<boolean>(false);
  const { setUser: clearUserCache, setLastFetched } = useUserCache();

  // auth initialization moved later to ensure refreshAccessToken is declared

  const login = async (
    username: string,
    password: string,
    rememberMe: boolean
  ) => {
    setLoading(true);
    try {
      try {
        await graphQLClient.cache.reset();
      } catch (e) {
        console.error("Error resetting Apollo client:", e);
      }

      const { data } = await authenticate({
        variables: { username, password },
      });

      const token = data.authenticate.accessToken;
      const refreshToken = data.authenticate.refreshToken;

      setAccessToken(token);
      setAuthenticated(true);

      // Extract user information from token
      const decoded = tokenService.decodeToken(token);
      const userId = decoded.userId;
      setIdentityId(userId);
      setRoles(decoded.roles);
      if (userId) setAnalyticsUserId(userId);

      // Store tokens using the appropriate storage type
      const storageType: StorageType = rememberMe ? "persistent" : "session";
      await Promise.all([
        tokenService.saveTokens(
          { accessToken: token, refreshToken, userId: userId || undefined },
          storageType
        ),
        // Set a flag that login is in progress to prevent immediate logout
        sessionStorage.setItem("auth_login_in_progress", "true"),
      ]);

      // Reset login flag after a delay
      setTimeout(() => {
        sessionStorage.removeItem("auth_login_in_progress");
      }, 5000);

      // Temporarily ignore auth errors during login transition
      window.__ignoreAuthErrors = true;
      setTimeout(() => {
        window.__ignoreAuthErrors = false;
      }, 2000);

      window.dispatchEvent(new CustomEvent("auth-change"));
    } finally {
      setLoading(false);
    }
  };

  const logout = useCallback(async () => {
    // Prevent recursive calls
    if (!isAuthenticated && !accessToken) return;

    try {
      // Clear state
      setIdentityId(null);
      setAccessToken(null);
      setAuthenticated(false);
      setRoles([]);
      clearAnalyticsUserId();

      // Clear user cache to prevent stale user data on re-login
      clearUserCache(null);
      setLastFetched(0);

      // Clear tokens
      await tokenService.clearTokens();

      // Clear other cached data
      await clearSharedCache(/^\/__storage\/[0-9a-f]{24}$/); // Pattern for chat IDs

      try {
        await graphQLClient.cache.reset();
      } catch (e) {
        console.error("Error resetting Apollo client cache:", e);
      }

      window.location.href = "/login";
    } catch (e) {
      console.error("Error during logout:", e);
      window.location.href = "/login";
    }
  }, [isAuthenticated, accessToken, clearUserCache, setLastFetched]);

  const refreshAccessToken = useCallback(async (forceRefreshToken?: string) => {
    // Use tokenService.startRefresh to dedupe multiple refresh attempts
    return tokenService.startRefresh(async () => {
      // Get the refresh token, either from parameter or from storage (may be null if server stores it HttpOnly)
      const refreshToken = forceRefreshToken || (await tokenService.getRefreshToken());

      try {
        // If we don't have a JS-visible refresh token, call mutation without variables
        // and rely on the server reading the HttpOnly cookie (credentials are included by Apollo client).
        const variables = refreshToken ? { refreshToken } : undefined;
        const { data } = await refreshTokenMutation({ variables } as any);

        const newAccessToken = data?.refreshToken?.accessToken;
        if (!newAccessToken) {
          // Clear tokens and force logout
          logout();
          return false;
        }

        // Update access token in state
        setAccessToken(newAccessToken);
        setAuthenticated(true);

        // Extract user data
        const decoded = tokenService.decodeToken(newAccessToken);
        setRoles(decoded.roles);
        setIdentityId(decoded.userId);
        if (decoded.userId) setAnalyticsUserId(decoded.userId);

        // Update tokens in storage - determine storage type from current state
        const storageType = sessionStorage.getItem("auth_tokens") ? "session" : "persistent";

        await tokenService.saveTokens(
          {
            accessToken: newAccessToken,
            // may be undefined if server is managing refresh token via HttpOnly cookie
            refreshToken: refreshToken || undefined,
            userId: decoded.userId || undefined,
          },
          storageType
        );

        window.dispatchEvent(new CustomEvent("auth-change"));
        return true;
      } catch (error) {
        logout();
        return false;
      }
    });
  }, [logout, refreshTokenMutation]);

  // Add a new effect to listen for forced token refresh events
  useEffect(() => {
    const handleForceTokenRefresh = async (event: any) => {
      const forceRefreshToken = event.detail?.refreshToken;
      const success = await refreshAccessToken(forceRefreshToken);
      
      if (success) {
        // Force token refresh successful
      } else {
        // Force token refresh failed, redirecting to login
        window.location.href = "/login?error=session_expired";
      }
    };

    window.addEventListener("force-token-refresh", handleForceTokenRefresh);
    
    return () => {
      window.removeEventListener("force-token-refresh", handleForceTokenRefresh);
    };
  }, [refreshAccessToken]);

  const hasRole = useCallback(
    (role: string): boolean => {
      return roles.includes(role);
    },
    [roles]
  );

  // Simplified token validation with better reliability
  useEffect(() => {
    // Skip validation during login process or if not initialized
    if (sessionStorage.getItem("auth_login_in_progress") === "true" || !authInitialized) {
      return;
    }

    // Track last validation time to prevent excessive checks
    let lastValidationTime = 0;

    const validateTokens = async () => {
      // Prevent validation if checked within last 30 seconds
      const now = Date.now();
      if (now - lastValidationTime < 30000) {
        return;
      }
      
      lastValidationTime = now;

      try {
        const tokens = await tokenService.getTokens();
        
        if (!tokens) {
          if (isAuthenticated) {
            logout();
          }
          return;
        }

        // Check if access token is valid or needs refresh
        if (!tokenService.isTokenValid(tokens.accessToken)) {
          await refreshAccessToken();
        } else {
          // Check if token expires soon (within 10 minutes)
          const decoded: any = JSON.parse(atob(tokens.accessToken.split(".")[1]));
          const currentTime = Date.now() / 1000;
          const timeLeft = decoded.exp - currentTime;

          if (timeLeft < 600) { // 10 minutes
            await refreshAccessToken();
          }
        }
      } catch (error) {
        console.error("❌ Error during token validation:", error);
        // Don't logout on validation errors - might be temporary
      }
    };

    // Validate tokens when page becomes visible (but not on every focus)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        validateTokens();
      }
    };

    // Only validate on window focus if tab was hidden for >5 minutes
    let lastHiddenTime = 0;
    const handleFocus = () => {
      const timeSinceHidden = Date.now() - lastHiddenTime;
      if (timeSinceHidden > 300000) { // 5 minutes
        validateTokens();
      }
    };

    const handleBlur = () => {
      lastHiddenTime = Date.now();
    };

    // Check tokens when page is restored from cache
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        validateTokens();
      }
    };

    // Initial validation
    validateTokens();

    // Set up periodic validation (less frequent to reduce load)
    const isPWA = window.matchMedia("(display-mode: standalone)").matches;
    const checkInterval = isPWA ? 300000 : 600000; // 5 or 10 minutes
    const intervalId = setInterval(validateTokens, checkInterval);

    // Add event listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [isAuthenticated, authInitialized, logout, refreshAccessToken]);

  // Handle authentication change events and service worker auth checks
  useEffect(() => {
    const handleAuthChange = async () => {
      const tokens = await tokenService.getTokens();

      if (!tokens) {
        if (isAuthenticated) {
          setAccessToken(null);
          setAuthenticated(false);
        }
        return;
      }

      if (tokenService.isTokenValid(tokens.accessToken)) {
        setAccessToken(tokens.accessToken);
        setAuthenticated(true);

        // Update user info
        const decoded = tokenService.decodeToken(tokens.accessToken);
        setRoles(decoded.roles);
        setIdentityId(decoded.userId);
      } else {
        await refreshAccessToken();
      }
    };

    const handleServiceWorkerAuthCheck = async () => {
      const tokens = await tokenService.getTokens();

      if (!tokens) {
        return;
      }

      if (!tokenService.isTokenValid(tokens.accessToken)) {
        await refreshAccessToken();
      } 
    };

    window.addEventListener("auth-change", handleAuthChange);
    window.addEventListener("sw-auth-check", handleServiceWorkerAuthCheck);

    return () => {
      window.removeEventListener("auth-change", handleAuthChange);
      window.removeEventListener("sw-auth-check", handleServiceWorkerAuthCheck);
    };
  }, [isAuthenticated, refreshAccessToken]);

  // Simplified auth initialization with better reliability
  useEffect(() => {
    const initAuthState = async () => {
      try {
        // Set a maximum timeout to prevent hanging
        const authInitTimeout = setTimeout(() => {
          console.warn("⚠️ Auth initialization timeout, proceeding without auth");
          setAuthInitialized(true);
        }, 5000);

        // Get tokens with a single, reliable approach
        const tokens = await tokenService.getTokens();
        clearTimeout(authInitTimeout);

        if (tokens?.accessToken) {
          // Check if token is valid or can be refreshed
          if (tokenService.isTokenValid(tokens.accessToken)) {
            setAccessToken(tokens.accessToken);
            setAuthenticated(true);

            const decoded = tokenService.decodeToken(tokens.accessToken);
            setIdentityId(decoded.userId);
            setRoles(decoded.roles);
            if (decoded.userId) setAnalyticsUserId(decoded.userId);
          } else {
            // Token expired or not present — attempt a refresh. Do not require a JS-accessible
            // refresh token because the server may provide a HttpOnly cookie for refresh.
            try {
              await refreshAccessToken();
            } catch (error) {
              await tokenService.clearTokens();
            }
          }
        } else {
          // Try to refresh using server-set HttpOnly cookie (if present)
          try {
            await refreshAccessToken();
          } catch (e) {
            // No cookie-driven session available
          }
        }
      } catch (error) {
        console.error("❌ Error during auth initialization:", error);
        // Don't clear tokens on error - might be temporary network issue
      } finally {
        setAuthInitialized(true);
      }
    };

    initAuthState();
  }, [refreshAccessToken]);

  // Registration function - keep as is
  const registerUser = async (
    name: string,
    lastname: string,
    username: string,
    password: string,
    invitationCode: string,
    birthDate: Date,
    phoneNumber: string,
    gender: string,
    acceptTerms: boolean,
    acceptPrivacy: boolean,
    acquisitionSource?: any,
    promoCode?: string,
    marketingOptIn?: string,
    whatsappOptIn?: boolean,
    clientTracking?: ClientTrackingInput
  ) => {
    setLoading(true);
    try {
      const { data, errors } = await createUser({
        variables: {
          name,
          lastname,
          username,
          password,
          invitationCode,
          birthDate,
          phoneNumber,
          gender,
          acceptTerms,
          acceptPrivacy,
          acquisitionSource,
          promoCode,
          marketingOptIn,
          whatsappOptIn,
          clientTracking,
        },
      });

      // Check for GraphQL errors
      if (errors && errors.length > 0) {
        throw new Error(errors[0].message);
      }

      // Return the created user
      return data?.createUser;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        identityId,
        login,
        logout,
        registerUser,
        accessToken,
        loading,
        hasRole,
        authInitialized,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
